// A firm's challenge settings, as Noctant staff see and change them. A change
// is made from the settings shown; when they changed since, the card reloads.

import { For, Show, createResource, createSignal } from "solid-js";

import AsyncResource from "./AsyncResource";
import {
  ApiError,
  api,
  failureSentence,
  type OrgChallengeSettings,
  type ReinstatementClock,
} from "../lib/api";
import { firmWords } from "../lib/orgWords";
import { useSession } from "../lib/session";

const REINSTATEMENT_CLOCKS: ReadonlyArray<[ReinstatementClock, string]> = [
  ["keep_original_deadline", "keeps its original deadline"],
  ["extend_by_time_failed", "gets back the days it spent failed"],
  ["restart_step_clock", "starts its time limit and inactivity clock again"],
];

type WhenReached = NonNullable<OrgChallengeSettings["live_limit"]["when_reached"]>;
type TraderKeysMayTrade = OrgChallengeSettings["trader_keys_may_trade"];

export default function ChallengeSettingsCard(props: {
  orgId: string;
  canEdit: boolean;
}) {
  const session = useSession();
  const words = () => firmWords(session.admin());
  const [shown, { mutate, refetch }] = createResource(
    () => props.orgId,
    (orgId) => api.getChallengeSettings(orgId),
  );
  const [limit, setLimit] = createSignal<string | null>(null);
  const [whenReached, setWhenReached] = createSignal<WhenReached | null>(null);
  const [clock, setClock] = createSignal<ReinstatementClock | null>(null);
  const [keys, setKeys] = createSignal<TraderKeysMayTrade | null>(null);
  const [reason, setReason] = createSignal("");
  const [message, setMessage] = createSignal<string | null>(null);

  const limitText = (settings: OrgChallengeSettings) =>
    settings.live_limit.max === null ? "" : String(settings.live_limit.max);
  // With no limit no choice is stored; the form offers today's, a refusal.
  const choiceShown = (settings: OrgChallengeSettings): WhenReached =>
    whenReached() ?? settings.live_limit.when_reached ?? "refuse";

  async function save(ev: Event, current: OrgChallengeSettings) {
    ev.preventDefault();
    const text = (limit() ?? limitText(current)).trim();
    const max = Number(text);
    if (text !== "" && (!Number.isInteger(max) || max < 1)) {
      setMessage("Enter a whole number of at least 1, or leave it empty.");
      return;
    }
    const next: OrgChallengeSettings = {
      ...current,
      reinstatement_clock: clock() ?? current.reinstatement_clock,
      trader_keys_may_trade: keys() ?? current.trader_keys_may_trade,
      live_limit:
        text === ""
          ? { max: null, when_reached: null }
          : { max, when_reached: choiceShown(current) },
    };
    try {
      mutate(
        await api.changeChallengeSettings(props.orgId, current, next, reason()),
      );
      setLimit(null);
      setWhenReached(null);
      setClock(null);
      setKeys(null);
      setReason("");
      setMessage("Saved.");
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        setLimit(null);
        setWhenReached(null);
        setClock(null);
        setKeys(null);
        void refetch();
        setMessage("The settings changed since they were shown; reloaded.");
        return;
      }
      setMessage(failureSentence(e));
    }
  }

  return (
    <div class="card">
      <div class="card-header">
        <h2 class="text-sm font-semibold">{words()?.run_label} settings</h2>
      </div>
      <div class="card-body">
        <AsyncResource resource={shown}>
          {(current) => (
            <form class="space-y-3" onSubmit={(ev) => void save(ev, current())}>
              <div>
                <label class="label" for="challenge-live-limit">
                  Most {words()?.runs} one trader may run at once (empty: no limit)
                </label>
                <input
                  id="challenge-live-limit"
                  class="input"
                  inputmode="numeric"
                  value={limit() ?? limitText(current())}
                  disabled={!props.canEdit}
                  onInput={(e) => setLimit(e.currentTarget.value)}
                />
              </div>
              <div>
                <label class="label" for="challenge-when-reached">
                  When a trader is at that number
                </label>
                <select
                  id="challenge-when-reached"
                  class="select"
                  value={choiceShown(current())}
                  disabled={!props.canEdit}
                  onChange={(e) =>
                    setWhenReached(e.currentTarget.value as WhenReached)
                  }
                >
                  <option value="refuse">A new {words()?.run} is refused</option>
                  <option value="allow_waiting">
                    It is bought and waits for a free slot, then starts by
                    itself
                  </option>
                </select>
              </div>
              <div>
                <label class="label" for="challenge-trader-keys">
                  Bots and agents (traders' API keys)
                </label>
                <select
                  id="challenge-trader-keys"
                  class="select"
                  value={keys() ?? current().trader_keys_may_trade}
                  disabled={!props.canEdit}
                  onChange={(e) =>
                    setKeys(e.currentTarget.value === "refused" ? "refused" : "allowed")
                  }
                >
                  <option value="allowed">Traders' API keys may trade</option>
                  <option value="refused">Traders' API keys may only read</option>
                </select>
              </div>
              <div>
                <label class="label" for="challenge-reinstatement-clock">
                  A reinstated {words()?.run}
                </label>
                <select
                  id="challenge-reinstatement-clock"
                  class="select"
                  value={clock() ?? current().reinstatement_clock}
                  disabled={!props.canEdit}
                  onChange={(e) => {
                    const value = e.currentTarget.value;
                    setClock(
                      REINSTATEMENT_CLOCKS.find(([key]) => key === value)?.[0] ??
                        null,
                    );
                  }}
                >
                  <For each={REINSTATEMENT_CLOCKS}>
                    {([value, label]) => <option value={value}>{label}</option>}
                  </For>
                </select>
              </div>
              <Show when={props.canEdit}>
                <div>
                  <label class="label" for="challenge-settings-reason">
                    Reason
                  </label>
                  <input
                    id="challenge-settings-reason"
                    class="input"
                    required
                    value={reason()}
                    onInput={(e) => setReason(e.currentTarget.value)}
                  />
                </div>
                <Show when={message()}>
                  <p class="text-sm text-slate-700">{message()}</p>
                </Show>
                <button type="submit" class="btn-primary">
                  Save {words()?.run} settings
                </button>
              </Show>
            </form>
          )}
        </AsyncResource>
      </div>
    </div>
  );
}
